import { Component, OnInit } from '@angular/core';
import { ChartDataService } from '@app/backend';
import { Subscription, Observable, startWith, map, tap } from 'rxjs';
import * as Highcharts from 'highcharts/highstock';
import * as charts from '@utilities/highchart-objectify';
import { ChartDataInterface, InventoryInterface } from '@app/models/chart-data.model';
import { UserService } from '@app/app-state/user.service';
import { SiteService } from '@app/app-state/site.service';
import { GenerateChartDataService } from '@app/backend/generate-chart-data.service';
import { FormControl } from '@angular/forms';
import { measurement } from '@lib/measurement';

export interface StockpileData {
  id: number;
  name: string;
  volume: number;
  createdAt: string;
  material?: string;
  density?: number;
  price?: number;
  materialType: MaterialType;
  materialPrice: MaterialPrice;
}

interface MaterialPrice {
	id: number;
	price: number;
	currency: string;
}

interface MaterialType {
	id: number;
	name: string;
	density: number;
}

interface InventoryItem extends ChartDataInterface {
  site?: { id: number; name: string };
  stockpile?: StockpileData;
  previousVolume?: number;
  previousDate?: string;
  daysBetween?: number;
  burnRate?: number | null;
  forecastDays?: number | null;
  forecastDate?: Date | null;
  cutVolume?: number;
  cutWeight?: number;
  totalPrice?: number;
  notes?: string;
}

interface Stockpile {
	id: number;
	name: string;
}

@Component({
  selector: 'app-inventory-dashboard',
  templateUrl: './inventory-dashboard.component.html',
  styleUrls: ['./inventory-dashboard.component.scss']
})

export class InventoryDashboardComponent implements OnInit {
	public Highcharts: typeof Highcharts = Highcharts; 
	public stockpileControl = new FormControl();
	public filteredStockpiles!: Observable<Stockpile[]>;
	private subscriptions: Subscription = new Subscription();
	public stockpiles: Stockpile[] = []
	public chartOptions = []

	public loading:boolean;

	private domainId: number;
	private subdomainId: number;
	public siteId: number;
	private startDate: string;
	private endDate: string;
	private selectedTimeline: string
	public inventoryData: ChartDataInterface[] = [];
	public tableColumns = [
		"site",
		"stockpile",
		"createdAt",
		"material",
		"density",
		"price",
		"cutVolume",
		"cutWeight",
		"totalPrice",
		"burnRate",
		"forecastDate",
		"notes"
	];

	public userUnitMeasurement: string;

	constructor(private readonly chartDataService: ChartDataService, 
		private siteService: SiteService, 
		private userService: UserService,
		private generateChartDataService:GenerateChartDataService,
	) {}

	ngOnInit() {
		this.subscriptions.add(this.chartDataService.loading$.subscribe(loading => {
			this.loading = loading;
		}));

		this.subscriptions.add(this.chartDataService.drillDetails$.subscribe((res) => {
			this.triggerLoadData(res);
		}));
		
		this.subscriptions.add(this.siteService.domainId$.subscribe(domainId => {
			this.domainId = domainId;
			if (this.domainId) {
				this.getData();
			}
		}));

		this.subscriptions.add(this.siteService.subDomainId$.subscribe(subdomainId => {
			this.subdomainId = subdomainId;
			this.triggerLoadData();
		}));

		this.subscriptions.add(this.siteService.siteId$.subscribe(siteId => {
			this.siteId = siteId;
			this.stockpiles = this.inventoryData.filter((data: any) => !this.siteId || data.site?.id === this.siteId).map((data: any) => ({ id: data.stockpile?.id, name: data.stockpile?.name })).filter(sp => sp.id != null && sp.name != null).reduce((acc: { id: number, name: string }[], sp) => {
				if (!acc.find(x => x.name === sp.name)) acc.push(sp);
				return acc;
			}, []);
			this.triggerLoadData();
		}));

		this.subscriptions.add(this.siteService.startDate$.subscribe(startDate => {
			this.startDate = startDate;
			this.triggerLoadData();
		}));

		this.subscriptions.add(this.userService.userUnitMeasurement$.subscribe((val: string) => {
			this.userUnitMeasurement = val;
			this.triggerLoadData();
		}));

		this.subscriptions.add(this.siteService.selectedTimeline$.subscribe(selectedTimeline => {
			this.selectedTimeline = selectedTimeline;
			this.triggerLoadData();
		})); 

		this.subscriptions.add(this.siteService.endDate$.subscribe(endDate => {
			this.endDate = endDate;
			this.triggerLoadData();
		}));

		this.filteredStockpiles = this.stockpileControl.valueChanges.pipe(
			startWith(''),
			tap(value => {
				if (value && typeof value === 'object') {
					this.siteService.stockpileId$.next(value.id);
					this.siteService.seletedStockpileName$.next(value.name);
				} else {
					this.siteService.stockpileId$.next(null);
					this.siteService.seletedStockpileName$.next(null);
				}
				this.triggerLoadData();
			}),
			map(value => (typeof value === 'string' ? value : value?.name)),
			map(name => (name ? this._filter(name) : this.stockpiles.slice()))
		);
	}

	private _filter(name: string) {
		const filterValue = name.toLowerCase();
		return this.stockpiles.filter(option => 
		  option.name.toLowerCase().includes(filterValue)
		);
	}
	
	public displayStockpileNameFn(stockpile: Stockpile) {
		return stockpile?.name || '';
	}

	private getData(){
		this.subscriptions.add(this.chartDataService.getCacheInventoryDetails().subscribe(details => {
			this.triggerLoadData(details);
		}))
	}

	private loadData = (chartData: ChartDataInterface[])=> {
		this.inventoryData = chartData;
		const data: InventoryInterface = this.generateChartDataService.generateChartData(this.inventoryData);
		for (const key in data?.inventoryGraphData) {
			this.chartOptions = charts.generateChartOptions(data.inventoryGraphData[key]);
			this.chartDataService.exportedGraphData$.next(data.inventoryGraphData[key]);
		}
	}

	private triggerLoadData(data? : ChartDataInterface[]) {
		const originalInventoryData = data || this.generateChartDataService.originalInventoryData;
		this.inventoryData = this.generateChartDataService.getFilteredData(
			originalInventoryData,
			this.domainId,
			this.subdomainId,
			this.siteId,
			this.startDate,
			this.endDate,
			this.selectedTimeline    
		);
		const inventoryWithBurnRate = this.addBurnRate()
		this.loadData(inventoryWithBurnRate);
	}

	private addBurnRate() {
		return this.inventoryData.map((item: InventoryItem, index: number, arr: InventoryItem[]) => {
			const volume = Number(item.stockpile?.volume) || 0;
			const density = Number(item.stockpile?.materialType?.density) || 0;
			const unitPrice = Number(item.stockpile?.materialPrice?.price) || 0;

			const weight = volume * density;
			const price = weight ? measurement.convert(weight, 'metricton') * unitPrice : 0;
		
			const previous = arr
				.filter((prev: InventoryItem) =>
				prev.stockpile?.name === item.stockpile?.name &&
					new Date(prev.stockpile?.createdAt || 0) < new Date(item.stockpile?.createdAt || 0)
				)
				.sort((a: InventoryItem, b: InventoryItem) =>
					new Date(b.stockpile?.createdAt || 0).getTime() -
					new Date(a.stockpile?.createdAt || 0).getTime()
			)[0];

			if (!previous) {
				return { ...item, burnRate: null, forecastDays: null, forecastDate: null, weight, price };
			}

			const daysBetween =
			(new Date(item.stockpile.createdAt).getTime() -
				new Date(previous.stockpile.createdAt).getTime()) /
			(1000 * 60 * 60 * 24);

			const previousVolume = previous.stockpile?.volume || 0;
			const currentVolume = item.stockpile?.volume || 0;

			// Only consider consumption (volume decrease)
			const burnRate = daysBetween > 0 ? Math.abs(previousVolume - currentVolume) / daysBetween : 0;
			const forecastDays = burnRate > 0 ? currentVolume / burnRate : null;
			const forecastDate =
			forecastDays !== null
				? new Date(new Date(item.stockpile.createdAt).getTime() + forecastDays * 86400000)
				: null;

			return {
				...item,
				previousVolume,
				previousDate: previous.stockpile.createdAt,
				daysBetween,
				burnRate,
				forecastDays,
				forecastDate,
				weight,
				price
			};
		});
	}

	ngOnDestroy() {
		this.subscriptions.unsubscribe();
	}

	chartRefs: Highcharts.Chart[] = [];
		
	onChartInstance(chart: Highcharts.Chart, index: number): void {
		this.chartRefs[index] = chart;
	}
}

